// src/utils/contactCache.js
// One shared memory of client contacts (sync_contacts) for the screens that show
// client names: the Home File Requests queue, the Transactions cards, the owner's
// Tuesday Call Sheet and follow-up plans.
//
// Why: each of those downloaded the same clients again, and a list that grew by
// one row (file requests arriving a moment after the deals) downloaded every
// client again. Now only clients not already in memory are downloaded, and lists
// that ask at the same moment share one download.
//
// Freshness comes first:
//  - Saving, adding or deleting a contact forgets it (firebasefunctions.js,
//    contactsApi.js). The next lookup reads it fresh from the server, and every
//    screen showing that client is told (onContactsForgotten) so it looks again.
//  - A download that was already on its way when the contact was saved is never
//    kept or shown.
//  - Memory expires after CONTACT_CACHE_TTL_MS, so edits made on another device
//    show up without a reload.
//
// Pure: no Firebase here. Callers pass the reader (getContactsByIds).

export const CONTACT_CACHE_TTL_MS = 5 * 60 * 1000;

const remembered = new Map(); // id -> { contact, at }
const onTheWay = new Map(); // id -> Promise<contact | null>
// Saves and lookups are put in order with a counter, not the clock: a lookup that
// starts in the same millisecond as a save must still count as after it.
let sequence = 0;
const forgottenAt = new Map(); // id -> sequence number of the save that forgot it
const listeners = new Set();

const cleanIds = (ids) => [
  ...new Set(
    (ids || [])
      .map((id) => (id == null ? "" : String(id).trim()))
      .filter(Boolean),
  ),
];

// Contacts for `ids`, in the order asked, leaving out any that do not exist.
export async function lookupContacts(ids, fetchContacts, { now = Date.now } = {}) {
  const wanted = cleanIds(ids);
  if (!wanted.length) return [];
  const startedAt = now();
  const startedSeq = ++sequence;
  const found = new Map();
  const waits = [];
  const toFetch = [];

  wanted.forEach((id) => {
    const hit = remembered.get(id);
    if (hit && startedAt - hit.at < CONTACT_CACHE_TTL_MS) {
      found.set(id, hit.contact);
    } else if (onTheWay.has(id)) {
      waits.push(onTheWay.get(id).then((c) => c && found.set(id, c)));
    } else {
      toFetch.push(id);
    }
  });

  if (toFetch.length) {
    const batch = Promise.resolve()
      .then(() => fetchContacts(toFetch))
      .then((list) => {
        const byId = new Map();
        (list || []).forEach((c) => {
          const key = c ? String(c.firebaseId || c.id || "") : "";
          if (key) byId.set(key, c);
        });
        return byId;
      });
    toFetch.forEach((id) => {
      const pending = batch
        .then((byId) => {
          const saved = forgottenAt.get(id);
          // Saved while this download was on its way: its copy is older than the save.
          if (saved !== undefined && saved > startedSeq) return null;
          const contact = byId.get(id) || null;
          if (contact) remembered.set(id, { contact, at: now() });
          return contact;
        })
        .finally(() => {
          if (onTheWay.get(id) === pending) onTheWay.delete(id);
        });
      onTheWay.set(id, pending);
      waits.push(pending.then((c) => c && found.set(id, c)));
    });
  }

  await Promise.all(waits);
  return wanted.map((id) => found.get(id)).filter(Boolean);
}

// A contact was saved, added or deleted: drop what memory holds for it and tell
// every screen showing it.
export function forgetContacts(ids) {
  const list = cleanIds(ids);
  if (!list.length) return;
  const at = ++sequence;
  list.forEach((id) => {
    remembered.delete(id);
    onTheWay.delete(id);
    forgottenAt.set(id, at);
  });
  listeners.forEach((listener) => {
    try {
      listener(list);
    } catch {
      // One screen's failure must not stop the others hearing about the save.
    }
  });
}

export function onContactsForgotten(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// For tests, and a clean slate between people signing in on one device.
export function clearContactCache() {
  remembered.clear();
  onTheWay.clear();
  forgottenAt.clear();
}
